import { useState, useCallback, useMemo } from 'react';

export interface PondPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type PondPositions = Record<string, PondPosition>;

interface UsePondLayoutReturn {
  positions: PondPositions;
  updatePosition: (pondId: string, x: number, y: number) => void;
  updateSize: (pondId: string, w: number, h: number) => void;
  saveLayout: () => void;
  resetLayout: () => void;
  factoryReset: () => void;
  hasSavedDefault: boolean;
  viewBox: { w: number; h: number };
}

const STORAGE_PREFIX = 'hiponhero:pond-layout:';
const SAVED_PREFIX = 'hiponhero:pond-layout-saved:';

// Default pond size
export const DEFAULT_W = 140;
export const DEFAULT_H = 100;
export const MIN_W = 80;
export const MIN_H = 50;
export const MAX_W = 280;
export const MAX_H = 200;

// Dike width between ponds (the earth walkways)
const DIKE = 24;

// Padding around the entire farm
const FARM_PAD = 80;

function generateFarmLayout(pondIds: string[]): PondPositions {
  if (pondIds.length === 0) return {};

  const cols = pondIds.length <= 2 ? 2
    : pondIds.length <= 6 ? 3
    : pondIds.length <= 12 ? 4
    : 5;

  const rowOffset = (DEFAULT_W + DIKE) / 2;

  const positions: PondPositions = {};
  pondIds.forEach((id, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const isOffsetRow = row % 2 === 1;
    const xShift = isOffsetRow ? rowOffset : 0;

    positions[id] = {
      x: FARM_PAD + col * (DEFAULT_W + DIKE) + xShift,
      y: FARM_PAD + row * (DEFAULT_H + DIKE),
      w: DEFAULT_W,
      h: DEFAULT_H,
    };
  });

  return positions;
}

/** Place new ponds below all existing ones so they never overlap. */
function placeInFreeSpace(newIds: string[], existing: PondPositions): PondPositions {
  if (newIds.length === 0) return {};

  const existingVals = Object.values(existing);
  if (existingVals.length === 0) return generateFarmLayout(newIds);

  // Start below all existing ponds
  const maxY = Math.max(...existingVals.map((p) => p.y + p.h));
  const startY = maxY + DIKE;

  const cols = newIds.length <= 2 ? 2
    : newIds.length <= 6 ? 3
    : newIds.length <= 12 ? 4
    : 5;

  const positions: PondPositions = {};
  newIds.forEach((id, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);

    positions[id] = {
      x: FARM_PAD + col * (DEFAULT_W + DIKE),
      y: startY + row * (DEFAULT_H + DIKE),
      w: DEFAULT_W,
      h: DEFAULT_H,
    };
  });

  return positions;
}

function computeViewBox(positions: PondPositions): { w: number; h: number } {
  const vals = Object.values(positions);
  if (vals.length === 0) return { w: 800, h: 500 };
  const maxX = Math.max(...vals.map((p) => p.x + p.w)) + FARM_PAD;
  const maxY = Math.max(...vals.map((p) => p.y + p.h)) + FARM_PAD;
  return { w: Math.max(800, maxX), h: Math.max(400, maxY) };
}

function loadSaved(farmId: string, prefix: string = STORAGE_PREFIX): PondPositions | null {
  try {
    const raw = localStorage.getItem(prefix + farmId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, Partial<PondPosition>>;
    // Ensure w/h exist (backcompat with old saves that only had x/y)
    const result: PondPositions = {};
    for (const [id, p] of Object.entries(parsed)) {
      result[id] = {
        x: p.x ?? 0,
        y: p.y ?? 0,
        w: p.w ?? DEFAULT_W,
        h: p.h ?? DEFAULT_H,
      };
    }
    return result;
  } catch {
    return null;
  }
}

function save(farmId: string, positions: PondPositions) {
  try {
    localStorage.setItem(STORAGE_PREFIX + farmId, JSON.stringify(positions));
  } catch {
    // localStorage full or unavailable
  }
}

/** Reconcile base positions with current pond list (add new, drop removed). */
function reconcile(base: PondPositions, sortedPondIds: string[]): PondPositions {
  const currentIds = new Set(sortedPondIds);
  const baseIds = new Set(Object.keys(base));

  const newIds = sortedPondIds.filter((id) => !baseIds.has(id));
  const hasRemoved = [...baseIds].some((id) => !currentIds.has(id));

  if (newIds.length === 0 && !hasRemoved) return base;

  const result: PondPositions = {};
  // Keep only current ponds
  for (const id of sortedPondIds) {
    if (base[id]) result[id] = base[id];
  }
  // Generate positions for new ponds in free space (no overlap)
  if (newIds.length > 0) {
    Object.assign(result, placeInFreeSpace(newIds, result));
  }
  return result;
}

export function usePondLayout(
  farmId: string | null,
  ponds: { id: string; status: string; name: string }[],
): UsePondLayoutReturn {
  const sortedPondIds = useMemo(() => {
    return [...ponds].sort((a, b) => a.name.localeCompare(b.name)).map((p) => p.id);
  }, [ponds]);

  const [basePositions, setBasePositions] = useState<PondPositions>(() => {
    const defaultPos = generateFarmLayout(sortedPondIds);
    if (!farmId) return defaultPos;

    const saved = loadSaved(farmId);
    if (saved) {
      const missingIds = sortedPondIds.filter((id) => !saved[id]);
      if (missingIds.length === 0) return saved;
      const extra = placeInFreeSpace(missingIds, saved);
      return { ...saved, ...extra };
    }

    return defaultPos;
  });

  // Derived: reconcile base positions with current pond list
  const positions = useMemo(
    () => reconcile(basePositions, sortedPondIds),
    [basePositions, sortedPondIds],
  );

  const viewBox = useMemo(() => computeViewBox(positions), [positions]);

  const [hasSavedDefault, setHasSavedDefault] = useState(() => {
    if (!farmId) return false;
    try { return localStorage.getItem(SAVED_PREFIX + farmId) !== null; } catch { return false; }
  });

  const updatePosition = useCallback(
    (pondId: string, x: number, y: number) => {
      setBasePositions((prev) => {
        const old = prev[pondId] ?? reconcile(prev, sortedPondIds)[pondId];
        if (!old) return prev;
        const next = { ...prev, [pondId]: { ...old, x, y } };
        if (farmId) save(farmId, next);
        return next;
      });
    },
    [farmId, sortedPondIds],
  );

  const updateSize = useCallback(
    (pondId: string, w: number, h: number) => {
      const clamped = {
        w: Math.max(MIN_W, Math.min(MAX_W, w)),
        h: Math.max(MIN_H, Math.min(MAX_H, h)),
      };
      setBasePositions((prev) => {
        const old = prev[pondId] ?? reconcile(prev, sortedPondIds)[pondId];
        if (!old) return prev;
        const next = { ...prev, [pondId]: { ...old, ...clamped } };
        if (farmId) save(farmId, next);
        return next;
      });
    },
    [farmId, sortedPondIds],
  );

  // Save current layout as the user's custom default
  const saveLayout = useCallback(() => {
    if (!farmId) return;
    try {
      localStorage.setItem(SAVED_PREFIX + farmId, JSON.stringify(positions));
      setHasSavedDefault(true);
    } catch { /* localStorage full */ }
  }, [farmId, positions]);

  // Reset to saved default (or factory if no saved default)
  const resetLayout = useCallback(() => {
    if (!farmId) return;
    const savedDefault = loadSaved(farmId, SAVED_PREFIX);
    if (savedDefault) {
      // Fill in any new ponds not in saved default
      const missingIds = sortedPondIds.filter((id) => !savedDefault[id]);
      const merged = missingIds.length > 0
        ? { ...savedDefault, ...placeInFreeSpace(missingIds, savedDefault) }
        : savedDefault;
      setBasePositions(merged);
      save(farmId, merged);
    } else {
      const fresh = generateFarmLayout(sortedPondIds);
      setBasePositions(fresh);
      localStorage.removeItem(STORAGE_PREFIX + farmId);
    }
  }, [farmId, sortedPondIds]);

  // Factory reset: clear everything back to auto-generated grid
  const factoryReset = useCallback(() => {
    const fresh = generateFarmLayout(sortedPondIds);
    setBasePositions(fresh);
    if (farmId) {
      localStorage.removeItem(STORAGE_PREFIX + farmId);
      localStorage.removeItem(SAVED_PREFIX + farmId);
      setHasSavedDefault(false);
    }
  }, [farmId, sortedPondIds]);

  return { positions, updatePosition, updateSize, saveLayout, resetLayout, factoryReset, hasSavedDefault, viewBox };
}
